import React from 'react';
import { DocumentRefitOutcome, useBuildHtmlDocumentRenderUrlMutation } from '~/api/graphql';
import type { WorkspacePdfFile } from '~/components/workspaces/artifact-focus-html-document-render-pdf-attachment';
import {
  buildAttachmentInputs,
  buildWorkspaceAttachmentCatalog,
  mergeAttachmentEntries,
  openRenderUrlInNewTab,
  type WorkspaceAttachableArtifact
} from '~/components/workspaces/lib/pdf-attachment-export';

/** Per-export overrides supplied by the caller via buildExtraInput. */
export type PdfRenderExportOverrides = {
  styleId?: string | null;
  producerInformationId?: string | null;
  skipTemplateFiles?: boolean;
  includeDesignPages?: boolean;
};

export function usePdfAttachmentExport(props: {
  workspaceId: string;
  artifactId: string;
  version: number;
  workspaceFiles?: WorkspacePdfFile[];
  workspaceMessageFiles?: WorkspacePdfFile[];
  workspaceArtifacts?: WorkspaceAttachableArtifact[];
  /** When set, resets prefix/suffix to these IDs on change. */
  defaultPrefixFileIds?: string[];
  defaultSuffixFileIds?: string[];
  resetKey?: string;
  buildExtraInput?: () => PdfRenderExportOverrides;
  /** Optional override for initial session catalog on reset (e.g. HTML template prefix/suffix files). */
  buildInitialCatalog?: () => Map<string, WorkspacePdfFile>;
  /** False while the editor has unsaved text or a historical/final version is selected. */
  refitDesign?: boolean;
}) {
  const {
    workspaceId,
    artifactId,
    version,
    workspaceFiles,
    workspaceMessageFiles,
    workspaceArtifacts,
    defaultPrefixFileIds = [],
    defaultSuffixFileIds = [],
    resetKey = `${artifactId}:${version}`,
    buildExtraInput,
    buildInitialCatalog,
    refitDesign = false
  } = props;

  const [prefixFileIds, setPrefixFileIds] = React.useState<string[]>(defaultPrefixFileIds);
  const [suffixFileIds, setSuffixFileIds] = React.useState<string[]>(defaultSuffixFileIds);
  const [localFiles, setLocalFiles] = React.useState<Map<string, File>>(() => new Map());
  const [sessionCatalog, setSessionCatalog] = React.useState<Map<string, WorkspacePdfFile>>(
    () => new Map()
  );
  const [isOpening, setIsOpening] = React.useState(false);
  const [openError, setOpenError] = React.useState<string | null>(null);
  const [openNotice, setOpenNotice] = React.useState<string | null>(null);
  const refitDesignRef = React.useRef(refitDesign);
  refitDesignRef.current = refitDesign;

  const [buildRenderUrl] = useBuildHtmlDocumentRenderUrlMutation();

  const buildExtraInputRef = React.useRef(buildExtraInput);
  buildExtraInputRef.current = buildExtraInput;
  const buildInitialCatalogRef = React.useRef(buildInitialCatalog);
  buildInitialCatalogRef.current = buildInitialCatalog;

  React.useEffect(() => {
    setPrefixFileIds(defaultPrefixFileIds);
    setSuffixFileIds(defaultSuffixFileIds);
    setLocalFiles(new Map());
    // A notice or an error about one version's PDF says nothing about the next.
    setOpenNotice(null);
    setOpenError(null);
    setSessionCatalog(
      buildInitialCatalogRef.current?.() ??
        buildWorkspaceAttachmentCatalog(
          workspaceFiles,
          workspaceMessageFiles,
          workspaceArtifacts,
          artifactId
        )
    );
  }, [resetKey]);

  const workspaceMergeKey = React.useMemo(
    () =>
      JSON.stringify({
        wf: (workspaceFiles ?? []).map((f) => ({ id: f.id, name: f.name, mimeType: f.mimeType })),
        wm: (workspaceMessageFiles ?? []).map((f) => ({
          id: f.id,
          name: f.name,
          mimeType: f.mimeType
        })),
        wa: (workspaceArtifacts ?? []).map((a) => ({
          id: a.id,
          version: a.version,
          type: a.type,
          title: a.title
        }))
      }),
    [workspaceFiles, workspaceMessageFiles, workspaceArtifacts]
  );

  React.useEffect(() => {
    setSessionCatalog((prev) => {
      const next = new Map<string, WorkspacePdfFile>(prev);
      mergeAttachmentEntries(
        next,
        workspaceFiles,
        workspaceMessageFiles,
        workspaceArtifacts,
        artifactId
      );
      return next;
    });
  }, [workspaceMergeKey, artifactId, workspaceFiles, workspaceMessageFiles, workspaceArtifacts]);

  const attachmentCatalogList = React.useMemo(() => [...sessionCatalog.values()], [sessionCatalog]);

  const onAttachmentChange = React.useCallback((p: string[], s: string[]) => {
    setPrefixFileIds(p);
    setSuffixFileIds(s);
  }, []);

  const handleOpenPdf = React.useCallback(async () => {
    // Opened before the wait: a tab opened once a refit answers, up to a minute later, has lost
    // the click that allows it and is blocked as a popup.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    setIsOpening(true);
    setOpenError(null);
    setOpenNotice(null);
    const refit = refitDesignRef.current;
    try {
      const extra = buildExtraInputRef.current?.() ?? {};
      const result = await buildRenderUrl({
        variables: {
          input: {
            workspaceId,
            artifactId,
            version,
            ...(refit && { refitDesign: true }),
            styleId: extra.styleId ?? null,
            producerInformationId: extra.producerInformationId ?? null,
            skipTemplateFiles: extra.skipTemplateFiles ?? true,
            // Left out unless asked for, so the request is unchanged for an agency without designs.
            ...(extra.includeDesignPages && { includeDesignPages: true }),
            prefixAttachments: buildAttachmentInputs(prefixFileIds, localFiles, sessionCatalog),
            suffixAttachments: buildAttachmentInputs(suffixFileIds, localFiles, sessionCatalog)
          }
        }
      });

      const rendered = result.data?.buildHtmlDocumentRenderUrl;
      if (!rendered?.url)
        throw new Error(result.errors?.[0]?.message ?? 'No URL returned from server');
      setOpenNotice(previousLayoutNotice(rendered.outcome));
      if (tab) tab.location.href = rendered.url;
      else openRenderUrlInNewTab(rendered.url);
    } catch (err) {
      tab?.close();
      setOpenError(err instanceof Error ? err.message : 'Failed to open PDF');
    } finally {
      setIsOpening(false);
    }
  }, [
    buildRenderUrl,
    workspaceId,
    artifactId,
    version,
    prefixFileIds,
    suffixFileIds,
    localFiles,
    sessionCatalog
  ]);

  return {
    prefixFileIds,
    suffixFileIds,
    localFiles,
    setLocalFiles,
    sessionCatalog,
    attachmentCatalogList,
    onAttachmentChange,
    isOpening,
    openError,
    openNotice,
    handleOpenPdf
  };
}

/** Says when the PDF did not get the Design's layout, and why. Null when no fit was asked for. */
export const previousLayoutNotice = (
  outcome: DocumentRefitOutcome | null | undefined
): string | null => {
  switch (outcome) {
    case DocumentRefitOutcome.RefitPending:
      return 'This PDF uses the previous layout. The document is still updating to its Design; open the PDF again in a moment.';
    case DocumentRefitOutcome.RefitFailed:
      return "This PDF uses the previous layout. The document couldn't be fitted to its Design.";
    case DocumentRefitOutcome.Changed:
      return 'This PDF uses the previous layout because the document changed while updating.';
    default:
      return null;
  }
};
